import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { type ColumnDef, type Row, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { DataTable, DataTablePagination, simpleTextColumn, useTableDisplayState } from '@/components/DataTable';
import { FillScreen } from '@/components/Layout';
import { AppUserColumn } from '@/features/app-user/components/AppUserColumn';
import { PersonColumn } from '@/features/app-user/components/PersonColumn';
import { usePaginationSearch, useSortingSearch } from '@/lib/listPage';
import { ActionsColumn } from './-components/column/actionColumn';
import { useLicenseServiceAccounts } from './-query';
import type { AccountTableRow } from './-types';
const SearchSchema = z.object({
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  sortColumn: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional()
});
export const Route = createFileRoute('/_staff/licenses/$id/service-plans/$licenseServiceId/_detail/accounts/')({
  component: ServicePlanAccounts,
  validateSearch: zodValidator(SearchSchema)
});
const columns: ColumnDef<AccountTableRow>[] = [AppUserColumn({
  i18nKey: 'appUser',
  minWidth: '360px',
  maxWidth: '400px'
}), PersonColumn('worker', {
  i18nKey: 'person',
  minWidth: '360px',
  maxWidth: '400px'
}), simpleTextColumn('displayName', {
  i18nKey: 'displayName'
}), ActionsColumn()];
function ServicePlanAccounts() {
  const {
    id: licenseId,
    licenseServiceId
  } = useParams({
    from: '/_staff/licenses/$id/service-plans/$licenseServiceId/_detail/accounts/'
  });
  const search = useSearch({
    from: '/_staff/licenses/$id/service-plans/$licenseServiceId/_detail/accounts/'
  });
  const navigate = useNavigate({
    from: '/licenses/$id/service-plans/$licenseServiceId/accounts'
  });
  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: search => navigate({
      search
    })
  });
  const [sorting, setSorting] = useSortingSearch({
    search,
    onSearchChange: search => navigate({
      search
    })
  });
  const {
    data,
    isPending
  } = useLicenseServiceAccounts({
    licenseId,
    licenseServiceId,
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    orderBy: search.sortColumn,
    orderDirection: search.sortOrder
  });
  const table = useReactTable({
    data: data?.items ?? [],
    pageCount: data?.maxPage,
    columns,
    initialState: {
      columnPinning: {
        left: ['user'],
        right: ['actions']
      }
    },
    state: {
      pagination,
      sorting
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true
  });
  const displayState = useTableDisplayState(isPending, data?.items ?? []);
  const handleRowClick = (row: Row<AccountTableRow>) => {
    if (!row.original.worker?.id) return;
    navigate({
      to: '/person/$id',
      params: {
        id: row.original.worker.id
      }
    });
  };
  return <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <div className="overflow-auto flex-1 min-h-0">
        <DataTable table={table} displayState={displayState} onRowClick={handleRowClick} />
      </div>
      <DataTablePagination table={table} totalCount={data?.count ?? 0} />
    </FillScreen>;
}